/**
 * Manifest parsing — reads SKILL.md, AGENT.md, agent.yaml, CONTRACT.md,
 * CONNECTOR.md, *.prompt.md, *.flow.yaml, *.bundle.yaml files and
 * produces CatalogEntry objects.
 * Used by @skaile/asset-manager for on-demand repo scanning.
 *
 * **All filesystem access here is async and bounded** (issue #454). A repo root
 * can resolve onto the rclone FUSE mount at `/skaile/workspace` — either
 * directly (a `sources[].path` pointing into the workspace) or indirectly, since
 * the walk follows symlinks and an off-mount tree can link into one. A
 * synchronous `readdirSync`/`statSync`/`readFileSync` there parks the whole
 * `skaile serve` event loop in an uninterruptible kernel wait, and a hang is not
 * an error so `try/catch` never fires. Every call goes through
 * {@link withFsDeadline}/{@link tryWithFsDeadline}, and the walk itself is
 * bounded by depth, entry count, a wall-clock budget, and an ancestor-inode set.
 */

import type { Dirent } from "node:fs";
import { opendir, readFile, stat } from "node:fs/promises";
import { basename, dirname, join } from "node:path";
import { parse } from "yaml";
import { fsDeadlineMs, tryWithFsDeadline, withFsDeadline } from "./fs-deadline.js";
import { createLogger } from "./logging/create-logger.js";
import type { AssetKind, CatalogEntry, Dependency } from "./models.js";
import { isValidAssetName, parseDep, slugifyAssetName } from "./models.js";

// ── Bounded manifest I/O ──────────────────────────────────────────────────────

/**
 * Read one manifest file under a deadline.
 *
 * @param filePath - Absolute path to the manifest.
 * @param timeoutMs - Per-operation deadline; defaults to {@link fsDeadlineMs}.
 * @throws {import("./fs-deadline.js").FsDeadlineError} when the read outlives the deadline.
 */
function readManifestFile(filePath: string, timeoutMs?: number): Promise<string> {
  return withFsDeadline(`readFile ${filePath}`, () => readFile(filePath, "utf8"), timeoutMs);
}

/**
 * Read one manifest file under a deadline, degrading an unreadable/slow file to
 * an empty document. Mirrors the pre-#454 `try { readFileSync } catch {}` shape
 * of the YAML parsers: a file we cannot read yields `{}` metadata rather than
 * aborting the caller.
 */
function readManifestFileOrEmpty(filePath: string, timeoutMs?: number): Promise<string> {
  return tryWithFsDeadline(`readFile ${filePath}`, () => readFile(filePath, "utf8"), "", timeoutMs);
}

// ── Frontmatter helpers ───────────────────────────────────────────────────────

/**
 * Result of parsing YAML frontmatter from a markdown file.
 * @docLink packages/core/concepts#frontmatter-result
 */
export interface FrontmatterResult {
  /** Parsed YAML data. Empty object if no frontmatter block is present. */
  data: Record<string, unknown>;
  /** Markdown body below the frontmatter block (trimmed). */
  body: string;
}

/**
 * Parse YAML frontmatter from a markdown file (`---` delimited).
 * Returns `{ data: {}, body: text }` when no frontmatter block is present.
 *
 * @param text - Raw file contents
 * @returns Parsed frontmatter data and the remaining body text
 * @docLink packages/core/concepts#parse-frontmatter
 */
export function parseFrontmatter(text: string): FrontmatterResult {
  const m = text.match(/^---\s*\n([\s\S]*?)\n---/);
  if (!m) return { data: {}, body: text.trim() };
  try {
    const data = (parse(m[1]!) as Record<string, unknown>) ?? {};
    const body = text.slice(m[0].length).trim();
    return { data, body };
  } catch {
    return { data: {}, body: text.trim() };
  }
}

/** Extract version from frontmatter, checking both top-level and metadata.version. */
function extractVersion(meta: Record<string, unknown>): string {
  return String(
    meta.version ?? (meta.metadata as Record<string, unknown> | undefined)?.version ?? "",
  );
}

/**
 * Parse the `requires:` field from manifest frontmatter into typed `Dependency` objects.
 * Accepts a comma-separated string or an array of `"kind:name"` strings. Reads from either
 * the root or `metadata.requires` (matching `extractVersion`'s dual-location behavior) so
 * skill authors can follow the agentskills.io convention of placing skaile extensions under
 * `metadata:` while remaining backward-compatible with root-level declarations.
 *
 * @param meta - Parsed frontmatter data object
 * @returns Array of `Dependency` objects (empty when `requires` is absent)
 * @docLink packages/core/concepts#parse-requires
 */
export function parseRequires(meta: Record<string, unknown>): Dependency[] {
  const raw = meta.requires ?? (meta.metadata as Record<string, unknown> | undefined)?.requires;
  if (!raw) return [];
  const list =
    typeof raw === "string"
      ? raw.split(",").map((s) => s.trim())
      : Array.isArray(raw)
        ? (raw as string[])
        : [];
  return list.filter(Boolean).map(parseDep);
}

// ── agent.yaml dependency collectors ───────────────────────────────────────────

/**
 * Collect named sub-agent dependencies from an `agent.yaml` array field.
 * Each `{name, ...}` object entry becomes an `{kind: "agent", name}` dependency.
 * Covers both the new `requires[]` and legacy `dependencies[]` formats.
 */
function collectAgentDeps(raw: unknown, out: Dependency[]): void {
  if (!Array.isArray(raw)) return;
  for (const d of raw) {
    if (typeof d === "object" && d !== null && "name" in d) {
      out.push({ kind: "agent", name: String((d as Record<string, unknown>).name) });
    }
  }
}

/**
 * Collect string refs from an `agent.yaml` array field (`abilities[]`,
 * `contracts[]`) as `{kind, name}` dependencies, skipping empty entries.
 */
function collectStringRefs(raw: unknown, kind: Dependency["kind"], out: Dependency[]): void {
  if (!Array.isArray(raw)) return;
  for (const r of raw) {
    if (typeof r === "string" && r) out.push({ kind, name: r });
  }
}

/**
 * Collect catalog refs from an `agent.yaml` mixin field (`persona`, `rules`,
 * `knowledge`) as requires so `skaile install` pulls them automatically. Local
 * paths (`.`/`/`) are skipped — not catalog assets. Refs without a `kind:`
 * prefix get `defaultKind` injected; the publisher scope and `#pin` are stripped
 * so only `kind:name` is tracked.
 */
function collectMixinRefs(raw: unknown, defaultKind: string, out: Dependency[]): void {
  if (!Array.isArray(raw)) return;
  for (const r of raw) {
    if (typeof r !== "string" || !r) continue;
    if (r.startsWith(".") || r.startsWith("/")) continue; // local path — skip
    // Normalize: if no kind: prefix, inject the default kind
    const normalized = r.includes(":") ? r : `${defaultKind}:${r}`;
    // Only kind:name matters for requires — strip the publisher scope and #pin.
    const colonIdx = normalized.indexOf(":");
    const kind = normalized.slice(0, colonIdx).trim();
    let rest = normalized.slice(colonIdx + 1).trim();
    const hashIdx = rest.indexOf("#");
    if (hashIdx !== -1) rest = rest.slice(0, hashIdx).trim();
    let name: string;
    if (rest.startsWith("@")) {
      // Scoped form @publisher/name → name is the segment after the slash.
      const slashIdx = rest.indexOf("/");
      name = slashIdx !== -1 ? rest.slice(slashIdx + 1).trim() : "";
    } else {
      // Legacy name@publisher (deprecated) or a bare name.
      const atIdx = rest.indexOf("@");
      name = atIdx !== -1 ? rest.slice(0, atIdx).trim() : rest;
    }
    if (name) out.push({ kind, name });
  }
}

// ── Manifest parsers ──────────────────────────────────────────────────────────

/**
 * Parse a `SKILL.md` file into a `CatalogEntry`.
 * The `kind` field defaults to `"skill"` but can be overridden to `"agent"` or `"prompt"`
 * via the `type:` frontmatter key. Falls back to the parent directory name when `name:` is absent.
 *
 * Async since #454 — the file may live on the FUSE workspace mount.
 *
 * @param filePath - Absolute path to the `SKILL.md` file
 * @param repoName - Repository name used as the `repository` field of the entry
 * @returns Populated `CatalogEntry` with kind, name, description, version, and requires
 * @docLink packages/core/concepts#from-skill-md
 */
export async function fromSkillMd(filePath: string, repoName: string): Promise<CatalogEntry> {
  return fromSkillMdContent(await readManifestFile(filePath), repoName, filePath);
}

/** Pure {@link fromSkillMd} core over already-read content (no I/O). */
function fromSkillMdContent(content: string, repoName: string, filePath: string): CatalogEntry {
  const { data: meta } = parseFrontmatter(content);
  let kind = String(meta.type ?? "skill") as AssetKind;
  if (!["skill", "agent", "prompt", "persona", "ruleset", "knowledge"].includes(kind))
    kind = "skill";
  return {
    name: String(meta.name ?? basename(dirname(filePath))),
    kind,
    description: String(meta.description ?? ""),
    source: filePath,
    publisher: repoName,
    version: extractVersion(meta),
    requires: parseRequires(meta),
    dependencies: [],
  };
}

/**
 * Parse an `AGENT.md` file into a `CatalogEntry` with `kind: "agent"`.
 * Falls back to the parent directory name when `name:` is absent.
 *
 * Async since #454 — the file may live on the FUSE workspace mount.
 *
 * @param filePath - Absolute path to the `AGENT.md` file
 * @param repoName - Repository name used as the `repository` field of the entry
 * @returns Populated `CatalogEntry` with kind `"agent"`
 * @docLink packages/core/concepts#from-agent-md
 */
export async function fromAgentMd(filePath: string, repoName: string): Promise<CatalogEntry> {
  return fromAgentMdContent(await readManifestFile(filePath), repoName, filePath);
}

/** Pure {@link fromAgentMd} core over already-read content (no I/O). */
function fromAgentMdContent(content: string, repoName: string, filePath: string): CatalogEntry {
  const { data: meta } = parseFrontmatter(content);
  return {
    name: String(meta.name ?? basename(dirname(filePath))),
    kind: "agent",
    description: String(meta.description ?? ""),
    source: filePath,
    publisher: repoName,
    version: extractVersion(meta),
    requires: parseRequires(meta),
    dependencies: [],
  };
}

/**
 * Parse an `agent.yaml` file into a `CatalogEntry` with `kind: "agent"`.
 * Collects sub-agent dependencies from `requires[]` (new format) and `dependencies[]`
 * (legacy format), skill abilities from `abilities[]`, and contracts from `contracts[]`.
 *
 * Async since #454 — the file may live on the FUSE workspace mount.
 *
 * @param filePath - Absolute path to the `agent.yaml` file
 * @param repoName - Repository name used as the `repository` field of the entry
 * @returns Populated `CatalogEntry` with kind `"agent"` and full dependency graph
 * @docLink packages/core/concepts#from-agent-yaml
 */
export async function fromAgentYaml(filePath: string, repoName: string): Promise<CatalogEntry> {
  return fromAgentYamlContent(await readManifestFileOrEmpty(filePath), repoName, filePath);
}

/** Pure {@link fromAgentYaml} core over already-read content (no I/O). */
function fromAgentYamlContent(content: string, repoName: string, filePath: string): CatalogEntry {
  let meta: Record<string, unknown> = {};
  try {
    meta = (parse(content) as Record<string, unknown>) ?? {};
  } catch {}

  const requires: Dependency[] = [];

  // Sub-agent dependencies from requires[] (new format: {name, source, ...})
  collectAgentDeps(meta.requires, requires);
  // Legacy sub-agent dependencies from dependencies[] (old format: {name, ...})
  collectAgentDeps(meta.dependencies, requires);
  // Skill abilities referenced in the system prompt
  collectStringRefs(meta.abilities, "skill", requires);
  // Contracts referenced in the system prompt (co-located in skills dir)
  collectStringRefs(meta.contracts, "contract", requires);

  // Mixin composition fields — track catalog refs as requires so `skaile install`
  // automatically pulls them. Local paths (. or /) are skipped — not catalog assets.
  for (const [field, defaultKind] of [
    ["persona", "persona"],
    ["rules", "ruleset"],
    ["knowledge", "knowledge"],
  ] as const) {
    collectMixinRefs(meta[field], defaultKind, requires);
  }

  return {
    name: String(meta.name ?? basename(dirname(filePath))),
    kind: "agent",
    description: String(meta.description ?? ""),
    source: filePath,
    publisher: repoName,
    version: String(meta.version ?? ""),
    requires,
    dependencies: [],
  };
}

/**
 * Parse a `*.prompt.md` file into a `CatalogEntry` with `kind: "prompt"`.
 * The name defaults to the filename stem (without the `.prompt.md` suffix)
 * when no `name:` frontmatter key is present.
 *
 * Async since #454 — the file may live on the FUSE workspace mount.
 *
 * @param filePath - Absolute path to the `.prompt.md` file
 * @param repoName - Repository name used as the `repository` field of the entry
 * @returns Populated `CatalogEntry` with kind `"prompt"`
 * @docLink packages/core/concepts#from-prompt-md
 */
export async function fromPromptMd(filePath: string, repoName: string): Promise<CatalogEntry> {
  return fromPromptMdContent(await readManifestFile(filePath), repoName, filePath);
}

/** Pure {@link fromPromptMd} core over already-read content (no I/O). */
function fromPromptMdContent(content: string, repoName: string, filePath: string): CatalogEntry {
  const { data: meta } = parseFrontmatter(content);
  const name = String(meta.name ?? basename(filePath).replace(/\.prompt\.md$/, ""));
  return {
    name,
    kind: "prompt",
    description: String(meta.description ?? ""),
    source: filePath,
    publisher: repoName,
    version: extractVersion(meta),
    requires: parseRequires(meta),
    dependencies: [],
  };
}

/**
 * Parse a `*.flow.yaml` or `*.flow.json` file into a `CatalogEntry` with `kind: "flow"`.
 * The name resolves from `name:` or `id:` frontmatter, falling back to the filename stem.
 *
 * Async since #454 — the file may live on the FUSE workspace mount.
 *
 * @param filePath - Absolute path to the flow definition file
 * @param repoName - Repository name used as the `repository` field of the entry
 * @returns Populated `CatalogEntry` with kind `"flow"`
 * @docLink packages/core/concepts#from-flow-yaml
 */
export async function fromFlowYaml(filePath: string, repoName: string): Promise<CatalogEntry> {
  return fromFlowYamlContent(await readManifestFileOrEmpty(filePath), repoName, filePath);
}

/** Pure {@link fromFlowYaml} core over already-read content (no I/O). */
function fromFlowYamlContent(content: string, repoName: string, filePath: string): CatalogEntry {
  let meta: Record<string, unknown> = {};
  try {
    meta = (parse(content) as Record<string, unknown>) ?? {};
  } catch {}

  const stem = basename(filePath)
    .replace(/\.flow\.yaml$/, "")
    .replace(/\.flow\.json$/, "");
  return {
    name: String(meta.name ?? meta.id ?? stem),
    kind: "flow",
    description: String(meta.description ?? ""),
    source: filePath,
    publisher: repoName,
    version: String(meta.version ?? ""),
    requires: parseRequires(meta),
    dependencies: [],
  };
}

/**
 * Parse a `*.bundle.yaml` file into a `CatalogEntry` with `kind: "bundle"`.
 * Bundles declare a flat list of string `dependencies` (asset refs) rather than
 * typed `Dependency` objects; both `requires` and `dependencies` are populated.
 *
 * Async since #454 — the file may live on the FUSE workspace mount.
 *
 * @param filePath - Absolute path to the bundle YAML file
 * @param repoName - Repository name used as the `repository` field of the entry
 * @returns Populated `CatalogEntry` with kind `"bundle"`
 * @docLink packages/core/concepts#from-bundle-yaml
 */
export async function fromBundleYaml(filePath: string, repoName: string): Promise<CatalogEntry> {
  return fromBundleYamlContent(await readManifestFileOrEmpty(filePath), repoName, filePath);
}

/** Pure {@link fromBundleYaml} core over already-read content (no I/O). */
function fromBundleYamlContent(content: string, repoName: string, filePath: string): CatalogEntry {
  let meta: Record<string, unknown> = {};
  try {
    meta = (parse(content) as Record<string, unknown>) ?? {};
  } catch {}

  const name = String(meta.name ?? basename(filePath).replace(/\.bundle\.yaml$/, ""));

  const deps = meta.dependencies;
  const dependencies = Array.isArray(deps) ? (deps as string[]).filter(Boolean) : [];

  return {
    name,
    kind: "bundle",
    description: String(meta.description ?? ""),
    source: filePath,
    publisher: repoName,
    version: String(meta.version ?? ""),
    requires: parseRequires(meta),
    dependencies,
  };
}

/**
 * Extract a bundle's raw transitive dependency ref strings from its manifest
 * file — the union of its `requires` and `dependencies` lists, kept verbatim
 * (publisher/pin qualifiers preserved, comments dropped by the parser).
 *
 * Supports `*.bundle.yaml` / `*.flow.{yaml,json}` (top-level
 * `requires`/`dependencies`) and the `BUNDLE.md` frontmatter layout
 * (`requires`/`dependencies`, incl. nested under `metadata`). Unlike
 * {@link parseRequires}, refs are returned as-is — the resolver needs the full
 * `kind:name@<publisher>[#pin]` form, not a publisher-stripped {@link Dependency}.
 *
 * Async since #454 — the file may live on the FUSE workspace mount.
 *
 * @param filePath - Absolute path to a `.bundle.yaml`, `.flow.{yaml,json}`, or `BUNDLE.md`.
 * @returns Deduped list of ref strings; `[]` on parse failure or no deps.
 * @docLink packages/core/concepts#bundle-dep-refs
 */
export async function bundleDepRefs(filePath: string): Promise<string[]> {
  return bundleDepRefsFromContent(await readManifestFileOrEmpty(filePath), filePath);
}

/** Pure {@link bundleDepRefs} core over already-read content (no I/O). */
function bundleDepRefsFromContent(content: string, filePath: string): string[] {
  // `.bundle.yaml` / `.flow.yaml` / `.flow.json` carry their dep list as a
  // top-level document key (`requires`/`dependencies`); `BUNDLE.md` carries it
  // in frontmatter. YAML is a JSON superset, so `parse` reads `.flow.json` too.
  const wholeDoc = /\.(bundle\.yaml|flow\.yaml|flow\.json)$/.test(filePath);
  let meta: Record<string, unknown> = {};
  try {
    meta = wholeDoc
      ? ((parse(content) as Record<string, unknown>) ?? {})
      : parseFrontmatter(content).data;
  } catch {
    return [];
  }
  const out: string[] = [];
  const collect = (v: unknown) => {
    if (Array.isArray(v))
      for (const x of v) if (typeof x === "string" && x.trim()) out.push(x.trim());
  };
  collect(meta.requires);
  collect((meta.metadata as Record<string, unknown> | undefined)?.requires);
  collect(meta.dependencies);
  return [...new Set(out)];
}

/**
 * Parse a `CONTRACT.md` file into a `CatalogEntry` with `kind: "contract"`.
 * Falls back to the parent directory name when `name:` is absent.
 *
 * Async since #454 — the file may live on the FUSE workspace mount.
 *
 * @param filePath - Absolute path to the `CONTRACT.md` file
 * @param repoName - Repository name used as the `repository` field of the entry
 * @returns Populated `CatalogEntry` with kind `"contract"`
 * @docLink packages/core/concepts#from-contract-md
 */
export async function fromContractMd(filePath: string, repoName: string): Promise<CatalogEntry> {
  return fromContractMdContent(await readManifestFile(filePath), repoName, filePath);
}

/** Pure {@link fromContractMd} core over already-read content (no I/O). */
function fromContractMdContent(content: string, repoName: string, filePath: string): CatalogEntry {
  const { data: meta } = parseFrontmatter(content);
  return {
    name: String(meta.name ?? basename(dirname(filePath))),
    kind: "contract",
    description: String(meta.description ?? ""),
    source: filePath,
    publisher: repoName,
    version: extractVersion(meta),
    requires: parseRequires(meta),
    dependencies: [],
  };
}

/**
 * Parse an `MCP.md` file into a `CatalogEntry` with `kind: "mcp-server"`.
 * Falls back to the parent directory name when `name:` is absent.
 *
 * Frontmatter fields (catalog identity):
 *   - `name`        — server name (default: parent dir name)
 *   - `description` — short description
 *   - `version`     — semver
 *   - `keywords`    — array of discovery tags
 *
 * Frontmatter fields (runtime defaults, stored in `metadata`):
 *   - `transport`   — "stdio" | "sse" | "http" (default: "stdio")
 *   - `command`     — command to execute (stdio)
 *   - `args`        — command arguments (stdio)
 *   - `env`         — environment variables (stdio)
 *   - `url`         — server URL (sse/http)
 *   - `headers`     — HTTP headers (sse/http)
 *
 * When `mcp:name` appears in `skaile.yaml` `dependencies`, the runner reads
 * these defaults from `metadata` to build a `McpServerDeclaration` without
 * requiring verbose inline config.
 *
 * Async since #454 — the file may live on the FUSE workspace mount.
 *
 * @param filePath - Absolute path to the `MCP.md` file
 * @param repoName - Repository name used as the `repository` field of the entry
 * @returns Populated `CatalogEntry` with kind `"mcp-server"`
 * @docLink packages/core/concepts#from-mcp-server-md
 */
export async function fromMcpServerMd(filePath: string, repoName: string): Promise<CatalogEntry> {
  return fromMcpServerMdContent(await readManifestFile(filePath), repoName, filePath);
}

/**
 * Parse pre-read MCP.md content into a `CatalogEntry`.
 * Callers that already hold the file content (e.g. to avoid a second read) use
 * this directly; `fromMcpServerMd` is a thin wrapper that reads then delegates here.
 *
 * @param content  - UTF-8 content of the `MCP.md` file
 * @param repoName - Repository name used as the `publisher` field of the entry
 * @param filePath - Absolute path (used as `source` and for name fallback)
 * @returns Populated `CatalogEntry` with kind `"mcp-server"`
 */
export function fromMcpServerMdContent(
  content: string,
  repoName: string,
  filePath: string,
): CatalogEntry {
  const { data: meta } = parseFrontmatter(content);

  // Extract runtime defaults into metadata
  const metadata: Record<string, unknown> = {};
  if (meta.transport) metadata.transport = String(meta.transport);
  if (meta.command) metadata.command = String(meta.command);
  if (Array.isArray(meta.args)) metadata.args = meta.args.map(String);
  if (meta.env && typeof meta.env === "object" && !Array.isArray(meta.env)) {
    metadata.env = meta.env as Record<string, string>;
  }
  if (meta.url) metadata.url = String(meta.url);
  if (meta.headers && typeof meta.headers === "object" && !Array.isArray(meta.headers)) {
    metadata.headers = meta.headers as Record<string, string>;
  }
  if (meta.recipe && typeof meta.recipe === "object" && !Array.isArray(meta.recipe)) {
    metadata.recipe = meta.recipe;
  }

  return {
    name: String(meta.name ?? basename(dirname(filePath))),
    kind: "mcp-server",
    description: String(meta.description ?? ""),
    source: filePath,
    publisher: repoName,
    version: extractVersion(meta),
    requires: parseRequires(meta),
    dependencies: [],
    metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
  };
}

/**
 * Parse a `knowledge.yaml` file into a `CatalogEntry` with `kind: "knowledge"`.
 * Falls back to the parent directory name when `name:` is absent.
 *
 * Async since #454 — the file may live on the FUSE workspace mount.
 *
 * @param filePath - Absolute path to the `knowledge.yaml` file
 * @param repoName - Repository name used as the `repository` field of the entry
 * @returns Populated `CatalogEntry` with kind `"knowledge"`
 * @docLink packages/core/concepts#from-knowledge-yaml
 */
export async function fromKnowledgeYaml(filePath: string, repoName: string): Promise<CatalogEntry> {
  return fromKnowledgeYamlContent(await readManifestFileOrEmpty(filePath), repoName, filePath);
}

/** Pure {@link fromKnowledgeYaml} core over already-read content (no I/O). */
function fromKnowledgeYamlContent(
  content: string,
  repoName: string,
  filePath: string,
): CatalogEntry {
  let meta: Record<string, unknown> = {};
  try {
    meta = (parse(content) as Record<string, unknown>) ?? {};
  } catch {}
  return {
    name: String(meta.name ?? basename(dirname(filePath))),
    kind: "knowledge",
    description: String(meta.description ?? ""),
    source: filePath,
    publisher: repoName,
    version: String(meta.version ?? ""),
    requires: parseRequires(meta),
    dependencies: [],
  };
}

/**
 * Parse a `CONNECTOR.md` file into a `CatalogEntry` with `kind: "connector"`.
 * Falls back to the parent directory name when `name:` is absent.
 *
 * Frontmatter fields (catalog identity):
 *   - `name`        — connector name (default: parent dir name)
 *   - `description` — short description
 *   - `version`     — semver
 *
 * Frontmatter fields (runtime defaults, stored in `metadata`):
 *   - `entry`        — adapter entry point (default: "./adapter.ts")
 *   - `npm_deps`     — npm dependencies required by the adapter
 *   - `fields`       — configuration fields the adapter expects
 *   - `health_check` — whether to run a health check on connect
 *
 * The markdown body (below the frontmatter) is stored in `metadata.body`
 * so the runner can inject the connector's skill description into the
 * system prompt when `expose_as_skill` is enabled.
 *
 * Async since #454 — the file may live on the FUSE workspace mount.
 *
 * @param filePath - Absolute path to the `CONNECTOR.md` file
 * @param repoName - Repository name used as the `repository` field of the entry
 * @returns Populated `CatalogEntry` with kind `"connector"`
 * @docLink packages/core/concepts#from-connector-md
 */
export async function fromConnectorMd(filePath: string, repoName: string): Promise<CatalogEntry> {
  return fromConnectorMdContent(await readManifestFile(filePath), repoName, filePath);
}

/** Pure {@link fromConnectorMd} core over already-read content (no I/O). */
function fromConnectorMdContent(content: string, repoName: string, filePath: string): CatalogEntry {
  const { data: meta, body } = parseFrontmatter(content);
  const name = String(meta.name ?? basename(dirname(filePath)));
  return {
    name,
    kind: "connector" as AssetKind,
    description: String(meta.description ?? ""),
    source: filePath,
    publisher: repoName,
    version: extractVersion(meta),
    requires: parseRequires(meta),
    dependencies: [],
    metadata: {
      entry: meta.entry ?? "./adapter.ts",
      npm_deps: meta.npm_deps ?? {},
      fields: meta.fields ?? [],
      health_check: meta.health_check ?? null,
      body,
    },
  };
}

// ── Recursive walker ──────────────────────────────────────────────────────────

interface WalkEntry {
  path: string;
  name: string;
}

/** Default maximum directory nesting the walk descends into. */
export const DEFAULT_WALK_MAX_DEPTH = 32;

/** Default maximum number of directory entries a single walk may examine. */
export const DEFAULT_WALK_MAX_ENTRIES = 50_000;

/** Default wall-clock budget for one whole walk / scan pass, in milliseconds. */
export const DEFAULT_WALK_BUDGET_MS = 60_000;

/** Env override for {@link DEFAULT_WALK_MAX_DEPTH}. */
export const WALK_MAX_DEPTH_ENV_VAR = "SKAILE_SCAN_MAX_DEPTH";

/** Env override for {@link DEFAULT_WALK_MAX_ENTRIES}. */
export const WALK_MAX_ENTRIES_ENV_VAR = "SKAILE_SCAN_MAX_ENTRIES";

/** Env override for {@link DEFAULT_WALK_BUDGET_MS}. */
export const WALK_BUDGET_MS_ENV_VAR = "SKAILE_SCAN_DEADLINE_MS";

/** Read a positive-integer env override, falling back when absent or unusable. */
function envInt(name: string, fallback: number): number {
  const raw = process.env[name];
  if (raw === undefined) return fallback;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Why a walk stopped early. Every one of these means the returned entries are a
 * **partial** view of the tree — never present a truncated walk as complete.
 *
 * @docLink packages/core/concepts#walk-dir
 */
export interface WalkTruncation {
  reason: "depth" | "entries" | "budget" | "cycle" | "unreadable";
  /** Directory (or symlink) at which the bound was hit. */
  path: string;
  /** The bound that was hit, when the reason has a numeric limit. */
  limit?: number;
}

/**
 * Bounds for {@link walkDir} / {@link scanDirectory}.
 *
 * @docLink packages/core/concepts#walk-dir
 */
export interface WalkOptions {
  /** Maximum nesting depth below the root. Default {@link DEFAULT_WALK_MAX_DEPTH}. */
  maxDepth?: number;
  /** Maximum directory entries examined across the whole walk. Default {@link DEFAULT_WALK_MAX_ENTRIES}. */
  maxEntries?: number;
  /**
   * Wall-clock budget for the whole walk. Default {@link DEFAULT_WALK_BUDGET_MS}.
   * The check is inclusive, so `0` yields no entries at all.
   */
  budgetMs?: number;
  /** Per-`opendir`/`stat`/`readFile` deadline. Default {@link fsDeadlineMs}. */
  opTimeoutMs?: number;
  /**
   * Called once per distinct truncation reason. Defaults to a `warn` on the
   * core logger — override to route the signal somewhere the caller can act on.
   */
  onTruncate?: (info: WalkTruncation) => void;
}

/** Mutable bookkeeping threaded through one walk. */
interface WalkState {
  maxDepth: number;
  maxEntries: number;
  opTimeoutMs: number | undefined;
  deadlineAt: number;
  examined: number;
  /**
   * `dev:ino` of the directories on the **current path from the root** — an
   * ancestor stack, not an ever-visited set. Re-entering an ancestor is a cycle;
   * re-reaching a directory off the current path is a diamond, and must be walked.
   */
  ancestors: Set<string>;
  reported: Set<WalkTruncation["reason"]>;
  onTruncate: (info: WalkTruncation) => void;
}

let walkLogger: ReturnType<typeof createLogger> | undefined;

/** Default truncation sink — a structured warning naming the bound that was hit. */
function warnTruncation(info: WalkTruncation): void {
  walkLogger ??= createLogger({ kind: "runner", subkind: "asset-scan" });
  walkLogger.warn(`asset scan truncated (${info.reason}) — results are incomplete`, {
    ...info,
  });
}

/** Record a truncation once per reason so a wide tree does not spam the log. */
function truncate(state: WalkState, info: WalkTruncation): void {
  if (state.reported.has(info.reason)) return;
  state.reported.add(info.reason);
  state.onTruncate(info);
}

/**
 * Stable identity (`dev:ino`) of the directory `path` resolves to, or `null`
 * when it cannot be stat'ed within the deadline. Used to terminate symlink
 * cycles — the pre-#454 walker recursed forever on `a/link -> a`.
 */
async function dirIdentity(path: string, timeoutMs: number | undefined): Promise<string | null> {
  const st = await tryWithFsDeadline(`stat ${path}`, () => stat(path), null, timeoutMs);
  return st ? `${st.dev}:${st.ino}` : null;
}

/**
 * Read one directory's entries under a deadline, capped at the walk's remaining
 * entry budget. Uses `opendir` rather than `readdir` so a pathological directory
 * streams instead of materialising in full; entries are collected and sorted
 * because the `scanDirectory` dedup rule ("alphabetically later source wins")
 * depends on a deterministic per-level order.
 */
async function readDirEntries(
  dir: string,
  state: WalkState,
): Promise<{ entries: Dirent[]; capped: boolean } | null> {
  const remaining = state.maxEntries - state.examined;
  if (remaining <= 0) return { entries: [], capped: true };
  try {
    return await withFsDeadline(
      `opendir ${dir}`,
      async () => {
        const handle = await opendir(dir);
        const entries: Dirent[] = [];
        let capped = false;
        for await (const e of handle) {
          // Stop *before* keeping the over-budget entry, so `capped` means "an
          // entry was actually dropped". Breaking after the push instead marked
          // a directory of exactly `remaining` entries as truncated — and since
          // `truncate()` reports once per reason for the whole walk, that
          // spurious first report swallowed a later genuine one.
          if (entries.length >= remaining) {
            capped = true;
            break;
          }
          entries.push(e);
        }
        entries.sort((a, b) => (a.name < b.name ? -1 : 1));
        return { entries, capped };
      },
      state.opTimeoutMs,
    );
  } catch {
    // Unreadable (permissions, vanished mid-walk) or the mount missed its
    // deadline. Either way this subtree contributes nothing; keep walking.
    truncate(state, { reason: "unreadable", path: dir });
    return null;
  }
}

/**
 * Recursively yield every file entry under `dir`, sorted alphabetically at each
 * level. Skips `.git` and `node_modules` directories and follows symlinks
 * (resolving to the stat target). `node_modules` is excluded because Bun's
 * `node_modules/.bun/...` layout contains symlink farms that form cycles when
 * packages reference each other transitively.
 *
 * **Async and bounded since #454.** A repo root can resolve onto the rclone FUSE
 * mount, where the previous synchronous `readdirSync`/`statSync` walk parked the
 * whole event loop. Four bounds now hold, and hitting any of them reports a
 * {@link WalkTruncation} — a truncated walk is never presented as a complete one:
 *
 * - **depth** ({@link WalkOptions.maxDepth}) — also the backstop for a cycle no
 *   inode check can see, e.g. a bind mount of the root inside itself.
 * - **entry count** ({@link WalkOptions.maxEntries}) — total directory entries
 *   examined, so an enormous tree cannot run unbounded.
 * - **wall-clock budget** ({@link WalkOptions.budgetMs}) — a wedged mount that
 *   times out one operation at a time cannot add up to an unbounded scan.
 * - **ancestor inodes** — the `dev:ino` of every directory on the path from the
 *   root is held while its subtree is walked, so `a/link -> a` terminates
 *   instead of recursing forever. Two sibling symlinks onto one shared
 *   directory are a diamond, not a cycle: both are walked, at the price of
 *   visiting the shared subtree twice — bounded by the entry and time budgets.
 *
 * @param dir - Absolute path to the directory to walk
 * @param options - Bounds and truncation sink; see {@link WalkOptions}
 * @docLink packages/core/concepts#walk-dir
 */
export async function* walkDir(dir: string, options: WalkOptions = {}): AsyncGenerator<WalkEntry> {
  const state: WalkState = {
    maxDepth: options.maxDepth ?? envInt(WALK_MAX_DEPTH_ENV_VAR, DEFAULT_WALK_MAX_DEPTH),
    maxEntries: options.maxEntries ?? envInt(WALK_MAX_ENTRIES_ENV_VAR, DEFAULT_WALK_MAX_ENTRIES),
    opTimeoutMs: options.opTimeoutMs,
    deadlineAt:
      Date.now() + (options.budgetMs ?? envInt(WALK_BUDGET_MS_ENV_VAR, DEFAULT_WALK_BUDGET_MS)),
    examined: 0,
    ancestors: new Set<string>(),
    reported: new Set<WalkTruncation["reason"]>(),
    onTruncate: options.onTruncate ?? warnTruncation,
  };
  // The root is an ancestor of everything, so it is seeded here and never
  // popped — a self-referential `root/link -> root` is caught on first descent.
  const rootId = await dirIdentity(dir, state.opTimeoutMs);
  if (rootId) state.ancestors.add(rootId);
  yield* walkFrom(dir, 0, state);
}

async function* walkFrom(dir: string, depth: number, state: WalkState): AsyncGenerator<WalkEntry> {
  if (depth > state.maxDepth) {
    truncate(state, { reason: "depth", path: dir, limit: state.maxDepth });
    return;
  }
  if (Date.now() >= state.deadlineAt) {
    truncate(state, { reason: "budget", path: dir });
    return;
  }

  const read = await readDirEntries(dir, state);
  if (!read) return;
  if (read.capped) truncate(state, { reason: "entries", path: dir, limit: state.maxEntries });

  for (const e of read.entries) {
    if (state.examined >= state.maxEntries) {
      truncate(state, { reason: "entries", path: dir, limit: state.maxEntries });
      return;
    }
    if (Date.now() >= state.deadlineAt) {
      truncate(state, { reason: "budget", path: dir });
      return;
    }
    state.examined++;
    const full = join(dir, e.name);

    if (e.isSymbolicLink()) {
      // A symlink is the only way a cycle can enter this walk, so identity is
      // resolved here and nowhere else — one extra stat per link, none per
      // ordinary directory.
      const st = await tryWithFsDeadline(`stat ${full}`, () => stat(full), null, state.opTimeoutMs);
      if (!st) continue;
      if (st.isDirectory()) {
        if (e.name === ".git" || e.name === "node_modules") continue;
        const id = `${st.dev}:${st.ino}`;
        // Only an *ancestor* is a cycle. A target already walked elsewhere in
        // the tree is a diamond (`a/link -> shared`, `b/link -> shared`) and
        // dropping it would silently lose that subtree from the scan.
        if (state.ancestors.has(id)) {
          truncate(state, { reason: "cycle", path: full });
          continue;
        }
        state.ancestors.add(id);
        try {
          yield* walkFrom(full, depth + 1, state);
        } finally {
          // finally, not a trailing delete: an early `return` from the bounds
          // checks — or an abandoned consumer — would otherwise leave the
          // ancestor stack holding a directory the walk has already left.
          state.ancestors.delete(id);
        }
      } else {
        yield { path: full, name: e.name };
      }
    } else if (e.isDirectory()) {
      if (e.name !== ".git" && e.name !== "node_modules") yield* walkFrom(full, depth + 1, state);
    } else if (e.isFile()) {
      yield { path: full, name: e.name };
    }
  }
}

// ── Public scanner ────────────────────────────────────────────────────────────

/**
 * Coerce a scanned entry's `name` to a canonical asset name in place. When the
 * raw name is already canonical this is a no-op; otherwise the original is stashed
 * on `metadata.displayName` (only when not already set) so callers keep the pretty
 * label for display while identity uses the slug. A name that slugifies to empty
 * is left untouched — `parseAssetRef` will reject it downstream with its full hint.
 */
function normalizeEntryName(e: CatalogEntry): void {
  if (isValidAssetName(e.name)) return;
  let slug: string;
  try {
    slug = slugifyAssetName(e.name);
  } catch {
    return;
  }
  const original = e.name;
  e.name = slug;
  if (e.metadata?.displayName === undefined) {
    e.metadata = { ...e.metadata, displayName: original };
  }
}

/**
 * Build a `CatalogEntry` from an already-read manifest, dispatching on filename.
 * Returns `null` when the filename is not a recognized manifest.
 */
function entryFromManifest(
  name: string,
  content: string,
  filePath: string,
  repoName: string,
): CatalogEntry | null {
  if (name === "SKILL.md") return fromSkillMdContent(content, repoName, filePath);
  if (name === "AGENT.md") return fromAgentMdContent(content, repoName, filePath);
  if (name === "agent.yaml") return fromAgentYamlContent(content, repoName, filePath);
  if (name === "CONTRACT.md") return fromContractMdContent(content, repoName, filePath);
  if (name === "MCP.md") return fromMcpServerMdContent(content, repoName, filePath);
  if (name === "CONNECTOR.md") return fromConnectorMdContent(content, repoName, filePath);
  if (name === "knowledge.yaml") return fromKnowledgeYamlContent(content, repoName, filePath);
  if (name.endsWith(".prompt.md")) return fromPromptMdContent(content, repoName, filePath);
  if (name.endsWith(".bundle.yaml")) return fromBundleYamlContent(content, repoName, filePath);
  if (name.endsWith(".flow.yaml") || name.endsWith(".flow.json"))
    return fromFlowYamlContent(content, repoName, filePath);
  return null;
}

/** Filenames {@link scanDirectory} recognises as asset manifests. */
function isManifestName(name: string): boolean {
  return (
    name === "SKILL.md" ||
    name === "AGENT.md" ||
    name === "agent.yaml" ||
    name === "CONTRACT.md" ||
    name === "MCP.md" ||
    name === "CONNECTOR.md" ||
    name === "knowledge.yaml" ||
    name.endsWith(".prompt.md") ||
    name.endsWith(".bundle.yaml") ||
    name.endsWith(".flow.yaml") ||
    name.endsWith(".flow.json")
  );
}

/**
 * Walk `root` recursively and return a `CatalogEntry` for every recognized asset manifest found.
 *
 * Recognized filenames: `SKILL.md`, `AGENT.md`, `agent.yaml`, `CONTRACT.md`, `MCP.md`,
 * `CONNECTOR.md`, `knowledge.yaml`, `*.prompt.md`, `*.bundle.yaml`, `*.flow.yaml`, `*.flow.json`.
 *
 * When two entries share the same `kind:name` key, the one with the alphabetically
 * later source path wins (consistent with catalog precedence rules).
 *
 * **Async and bounded since #454.** `root` can resolve onto the rclone FUSE mount
 * (a `sources[].path` under `/skaile/workspace`), and the walk follows symlinks, so
 * even an off-mount root can descend into one. Every `opendir`/`stat`/`readFile`
 * is deadline-bounded and the walk is capped by depth, entry count, a wall-clock
 * budget, and an ancestor-inode set — see {@link walkDir}. Hitting any bound reports
 * a {@link WalkTruncation} through {@link WalkOptions.onTruncate} (a `warn` by
 * default); the returned array is then a **partial** scan, never a complete one.
 *
 * A manifest that cannot be read within its deadline is skipped rather than
 * failing the whole scan — one unreachable file must not cost the whole catalog.
 *
 * @param root - Absolute path to the directory tree to scan
 * @param repoName - Repository name attached to every produced entry
 * @param options - Walk bounds and truncation sink; see {@link WalkOptions}
 * @returns Array of deduplicated `CatalogEntry` objects, one per manifest file
 * @docLink packages/core/concepts#scan-directory
 */
export async function scanDirectory(
  root: string,
  repoName: string,
  options: WalkOptions = {},
): Promise<CatalogEntry[]> {
  const entries = new Map<string, CatalogEntry>();
  const onTruncate = options.onTruncate ?? warnTruncation;
  const reported = new Set<WalkTruncation["reason"]>();
  const relay = (info: WalkTruncation) => {
    reported.add(info.reason);
    onTruncate(info);
  };
  const opTimeoutMs = options.opTimeoutMs ?? fsDeadlineMs();

  function add(e: CatalogEntry): void {
    // Boundary normalization: a third-party source we don't control may name an
    // asset with a human-styled identity ("Complex App"). Slugify it to a
    // canonical name so the ref it produces parses, preserving the original on
    // `metadata.displayName` for display. No-op for already-canonical names.
    normalizeEntryName(e);
    const key = `${e.kind}:${e.name}`;
    const existing = entries.get(key);
    if (!existing) {
      entries.set(key, e);
      return;
    }
    // Among duplicates, alphabetically later source path wins
    if (e.source > existing.source) entries.set(key, e);
  }

  for await (const { path: filePath, name } of walkDir(root, {
    ...options,
    opTimeoutMs,
    onTruncate: relay,
  })) {
    if (!isManifestName(name)) continue;
    const content = await tryWithFsDeadline(
      `readFile ${filePath}`,
      () => readFile(filePath, "utf8"),
      null,
      opTimeoutMs,
    );
    if (content === null) {
      // Unreadable or past its deadline — the catalog loses this asset, not the
      // whole scan. Reported once so a wedged mount is visible in the logs.
      if (!reported.has("unreadable")) relay({ reason: "unreadable", path: filePath });
      continue;
    }
    try {
      const entry = entryFromManifest(name, content, filePath, repoName);
      if (entry) add(entry);
    } catch {}
  }

  return [...entries.values()];
}
